import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { api, getTodayDate, expenseCategories, paymentModes } from '../lib/utils';
import { isOnline, saveRevenueOffline, saveExpenseOffline, saveReceivableOffline, getOfflineVentures, getOfflineLocations } from '../lib/offlineSync';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Autocomplete } from './ui/autocomplete';
import { IndianRupee, TrendingUp, TrendingDown, Users, WifiOff, Camera, Upload, Loader2, Sparkles } from 'lucide-react';

export default function QuickEntryDialog({ open, onOpenChange }) {
  const [activeTab, setActiveTab] = useState('revenue');
  const [ventures, setVentures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [vendorLibrary, setVendorLibrary] = useState([]);
  const allCategories = [...expenseCategories, ...customCategories.map(c => ({ value: c.value, label: c.label }))];
  const vendorSuggestions = vendorLibrary.map(v => v.name);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Revenue form
  const [revenueForm, setRevenueForm] = useState({
    date: getTodayDate(),
    venture_id: '',
    location_id: '',
    amount: '',
    notes: ''
  });

  // Expense form
  const [expenseForm, setExpenseForm] = useState({
    date: getTodayDate(),
    amount: '',
    vendor: '',
    category: '',
    venture_id: '',
    location_id: '',
    payment_mode: 'upi',
    notes: ''
  });

  // Receivable form
  const [receivableForm, setReceivableForm] = useState({
    customer_name: '',
    order_value: '',
    advance_received: '',
    delivery_date: '',
    venture_id: '',
    location_id: '',
    notes: ''
  });

  useEffect(() => {
    if (open) {
      fetchVenturesAndLocations();
    }
  }, [open]);

  const fetchVenturesAndLocations = async () => {
    try {
      if (isOnline()) {
        const [venturesRes, locationsRes, catsRes, vendorsRes] = await Promise.all([
          api.get('/ventures'),
          api.get('/locations'),
          api.get('/categories/custom').catch(() => ({ data: [] })),
          api.get('/library/vendors').catch(() => ({ data: [] }))
        ]);
        setVentures(venturesRes.data);
        setLocations(locationsRes.data);
        setCustomCategories(catsRes.data);
        setVendorLibrary(vendorsRes.data || []);
      } else {
        setVentures(getOfflineVentures());
        setLocations(getOfflineLocations());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setVentures(getOfflineVentures());
      setLocations(getOfflineLocations());
    }
  };

  const filteredLocations = (ventureId) => {
    return locations.filter(loc => loc.venture_id === ventureId);
  };

  // AI Bill Scanning
  const handleScanBill = async (file) => {
    if (!file) return;
    
    setScanLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/quick-entry/scan-bill', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 45000
      });
      
      const data = res.data;
      
      // Auto-fill the expense form with extracted data
      setExpenseForm(prev => ({
        ...prev,
        vendor: data.vendor || prev.vendor,
        amount: data.amount ? String(data.amount) : prev.amount,
        category: data.category || prev.category,
        date: data.date || prev.date,
        notes: data.notes || prev.notes
      }));
      
      toast.success('Bill scanned! Review and adjust if needed.', {
        icon: <Sparkles className="w-4 h-4 text-amber-500" />
      });
    } catch (error) {
      console.error('Scan error:', error);
      toast.error(error?.response?.data?.detail || 'Failed to scan bill. Try manual entry.');
    } finally {
      setScanLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleScanBill(file);
    e.target.value = '';
  };

  const handleRevenueSubmit = async (e) => {
    e.preventDefault();
    if (!revenueForm.venture_id || !revenueForm.location_id || !revenueForm.amount) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const revenueData = {
        ...revenueForm,
        amount: parseFloat(revenueForm.amount)
      };
      
      if (isOnline()) {
        await api.post('/revenues', revenueData);
        toast.success('Revenue recorded successfully');
      } else {
        await saveRevenueOffline(revenueData);
        toast.success('Revenue saved offline - will sync when online', {
          icon: <WifiOff className="w-4 h-4" />
        });
      }
      
      setRevenueForm({
        date: getTodayDate(),
        venture_id: '',
        location_id: '',
        amount: '',
        notes: ''
      });
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to record revenue');
    } finally {
      setLoading(false);
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseForm.venture_id || !expenseForm.amount || !expenseForm.vendor || !expenseForm.category) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const expenseData = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      };
      
      if (isOnline()) {
        await api.post('/expenses', expenseData);
        toast.success('Expense recorded successfully');
      } else {
        await saveExpenseOffline(expenseData);
        toast.success('Expense saved offline - will sync when online', {
          icon: <WifiOff className="w-4 h-4" />
        });
      }
      
      setExpenseForm({
        date: getTodayDate(),
        amount: '',
        vendor: '',
        category: '',
        venture_id: '',
        location_id: '',
        payment_mode: 'upi',
        notes: ''
      });
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to record expense');
    } finally {
      setLoading(false);
    }
  };

  const handleReceivableSubmit = async (e) => {
    e.preventDefault();
    if (!receivableForm.customer_name || !receivableForm.order_value || !receivableForm.venture_id) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const receivableData = {
        ...receivableForm,
        order_value: parseFloat(receivableForm.order_value),
        advance_received: parseFloat(receivableForm.advance_received || 0)
      };
      
      if (isOnline()) {
        await api.post('/receivables', receivableData);
        toast.success('Receivable recorded successfully');
      } else {
        await saveReceivableOffline(receivableData);
        toast.success('Receivable saved offline - will sync when online', {
          icon: <WifiOff className="w-4 h-4" />
        });
      }
      
      setReceivableForm({
        customer_name: '',
        order_value: '',
        advance_received: '',
        delivery_date: '',
        venture_id: '',
        location_id: '',
        notes: ''
      });
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to record receivable');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="quick-entry-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Quick Entry</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="revenue" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Revenue
            </TabsTrigger>
            <TabsTrigger value="expense" className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4" />
              Expense
            </TabsTrigger>
          </TabsList>

          {/* Revenue Tab */}
          <TabsContent value="revenue">
            <form onSubmit={handleRevenueSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={revenueForm.date}
                    onChange={(e) => setRevenueForm({ ...revenueForm, date: e.target.value })}
                    className="mt-1.5"
                    data-testid="revenue-date"
                  />
                </div>
                <div>
                  <Label>Amount (₹)</Label>
                  <div className="relative mt-1.5">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      type="number"
                      placeholder="0"
                      value={revenueForm.amount}
                      onChange={(e) => setRevenueForm({ ...revenueForm, amount: e.target.value })}
                      className="pl-9"
                      data-testid="revenue-amount"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Venture</Label>
                <Select
                  value={revenueForm.venture_id}
                  onValueChange={(value) => setRevenueForm({ ...revenueForm, venture_id: value, location_id: '' })}
                >
                  <SelectTrigger className="mt-1.5" data-testid="revenue-venture">
                    <SelectValue placeholder="Select venture" />
                  </SelectTrigger>
                  <SelectContent>
                    {ventures.map((v) => (
                      <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Location</Label>
                <Select
                  value={revenueForm.location_id}
                  onValueChange={(value) => setRevenueForm({ ...revenueForm, location_id: value })}
                  disabled={!revenueForm.venture_id}
                >
                  <SelectTrigger className="mt-1.5" data-testid="revenue-location">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredLocations(revenueForm.venture_id).map((loc) => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notes (optional)</Label>
                <Input
                  placeholder="Any additional notes..."
                  value={revenueForm.notes}
                  onChange={(e) => setRevenueForm({ ...revenueForm, notes: e.target.value })}
                  className="mt-1.5"
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="revenue-submit">
                {loading ? 'Recording...' : 'Record Revenue'}
              </Button>
            </form>
          </TabsContent>

          {/* Expense Tab */}
          <TabsContent value="expense">
            <div className="space-y-4 mt-4">
              {/* Scan Bill Section */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">AI Bill Scanner</span>
                </div>
                <p className="text-xs text-amber-700 mb-3">Take a photo or upload a bill to auto-fill details</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1 bg-white"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={scanLoading}
                    data-testid="scan-camera-btn"
                  >
                    {scanLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
                    Take Photo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1 bg-white"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={scanLoading}
                    data-testid="scan-upload-btn"
                  >
                    {scanLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Upload
                  </Button>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              </div>

              <form onSubmit={handleExpenseSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={expenseForm.date}
                      onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                      className="mt-1.5"
                      data-testid="expense-date"
                    />
                  </div>
                  <div>
                    <Label>Amount (₹)</Label>
                    <div className="relative mt-1.5">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        type="number"
                        placeholder="0"
                        value={expenseForm.amount}
                        onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                        className="pl-9"
                        data-testid="expense-amount"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Vendor</Label>
                  <Autocomplete
                    suggestions={vendorSuggestions}
                    value={expenseForm.vendor}
                    onValueChange={(value) => setExpenseForm({ ...expenseForm, vendor: value })}
                    placeholder="Vendor name"
                    inputClassName="mt-1.5"
                    data-testid="expense-vendor"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={expenseForm.category}
                      onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="expense-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {allCategories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Payment Mode</Label>
                    <Select
                      value={expenseForm.payment_mode}
                      onValueChange={(value) => setExpenseForm({ ...expenseForm, payment_mode: value })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="expense-payment">
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentModes.map((mode) => (
                          <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Venture</Label>
                  <Select
                    value={expenseForm.venture_id}
                    onValueChange={(value) => setExpenseForm({ ...expenseForm, venture_id: value, location_id: '' })}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="expense-venture">
                      <SelectValue placeholder="Select venture" />
                    </SelectTrigger>
                    <SelectContent>
                      {ventures.map((v) => (
                        <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Location</Label>
                  <Select
                    value={expenseForm.location_id}
                    onValueChange={(value) => setExpenseForm({ ...expenseForm, location_id: value })}
                    disabled={!expenseForm.venture_id}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="expense-location">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredLocations(expenseForm.venture_id).map((loc) => (
                        <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="w-full" disabled={loading} data-testid="expense-submit">
                  {loading ? 'Recording...' : 'Record Expense'}
                </Button>
              </form>
            </div>
          </TabsContent>

          {/* Receivable Tab */}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
